import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from "react";
import {
  AudioLines,
  BookOpen,
  Plus,
  Search,
  Copy,
  Trash2,
  Upload,
  Download,
  MonitorUp,
  Smartphone,
  Radio,
  Settings2,
  Play,
  Pause,
  SkipBack,
  ChevronLeft,
  ChevronRight,
  EyeOff,
  AlertTriangle,
  X,
  Check,
  Maximize,
  FileText,
  ListVideo,
  Flag,
  Sun,
  Moon,
  MoreHorizontal,
  Wifi,
  Link,
  GripVertical,
  ArrowUp,
  ArrowDown,
  PanelLeftClose,
  PanelLeftOpen,
  CircleHelp,
  RefreshCw,
  LockKeyhole,
  LockKeyholeOpen,
} from "lucide-react";
import type { StudioController } from "./useStudio";
import type { CueScript, ViewRole } from "./types";
import { LibrarySearch } from "./core/library-search";
import { PromptSurface } from "./components/PromptSurface";
import {
  deferredFeature,
  DeferredRecoveryContext,
} from "./components/DeferredFeature";
import { ScriptNavigator } from "./components/ScriptNavigator";
import { ReadingPosition } from "./components/ReadingPosition";
import { DragNumberInput } from "./components/DragNumberInput";
import { Launchpad } from "./components/Launchpad";
import { ProjectFolders, ScriptFolderPicker, UNFILED_PROJECT, type ProjectFilter } from "./components/ProjectFolders";
import { RehearsalControls } from "./components/RehearsalControls";
import { ProductionNotes } from "./components/ProductionNotes";
import './components/production-notes-page.css';
import { TalentControls } from "./components/TalentControls";
import { ListeningIndicator } from "./components/ListeningIndicator";
import { StudioControls, type ControlSection } from "./components/StudioControls";
import { ScriptExportMenu } from "./components/ScriptExportMenu";
import { BatchExportDialog } from "./components/BatchExportDialog";
import { AgentConnections } from './components/AgentConnections';
import type { WordRevealRequest } from "./components/useRevealScriptWord";

const RichEditor = deferredFeature<
  ComponentProps<typeof import("./components/RichEditor").RichEditor>
>(
  () =>
    import("./components/RichEditor").then((m) => ({ default: m.RichEditor })),
  "Script editor",
);
const duration = (seconds: number) =>
  `${Math.floor(Math.max(0, seconds) / 60)
    .toString()
    .padStart(2, "0")}:${Math.floor(Math.max(0, seconds) % 60)
    .toString()
    .padStart(2, "0")}`;
const iconHelp: Record<string, string> = {
  "Switch to light theme": "Use the light operator interface without changing the talent display colors.",
  "Switch to dark theme": "Use the dark operator interface without changing the talent display colors.",
  "Help and keyboard shortcuts": "Open setup guidance and the keyboard shortcuts for editing and prompting.",
  "Collapse script library": "Hide the script library to give the editor and monitor more room.",
  "Show script library": "Show saved scripts and your rundown beside the operator workspace.",
  "Find in script": "Find a word or phrase and move every display to its position.",
  "Unlock editing": "Allow changes to the script text, title and notes again.",
  "Lock editing": "Prevent accidental script edits while keeping prompting and navigation available.",
  "Fullscreen operator preview": "Expand the operator monitor to fullscreen. Press Esc to return to the desk.",
  "Restart script": "Return to the beginning of the script and reset the take timer.",
  "Back 5 words": "Move the reading position back five words. Shortcut: Left arrow outside text fields.",
  "Forward 5 words": "Move the reading position forward five words. Shortcut: Right arrow outside text fields.",
  "Show emergency message controls": "Open controls for a message that appears over the talent script.",
  "Close side panel": "Close this panel and return to the operator desk.",
};
function iconButtonHelp(label: string) {
  if (iconHelp[label]) return iconHelp[label];
  if (label.startsWith("Duplicate ")) return "Create a separate editable copy of this script in the library.";
  if (label.startsWith("Delete ")) return "Move this script to Recovery after confirmation. It can be restored from Tools.";
  if (label.startsWith("Add ") && label.endsWith(" to rundown")) return "Append this script to the shoot order. The original stays in the library.";
  if (label.startsWith("Move ") && label.endsWith(" earlier")) return "Move this entry one place earlier in the rundown.";
  if (label.startsWith("Move ") && label.endsWith(" later")) return "Move this entry one place later in the rundown.";
  if (label.startsWith("Remove ") && label.endsWith(" from rundown")) return "Remove this rundown entry while keeping the script in the library.";
  return label;
}
function IconButton({
  label,
  children,
  onClick,
  active = false,
  danger = false,
}: {
  label: string;
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  danger?: boolean;
}) {
  return (
    <button data-help={iconButtonHelp(label)}
      type="button"
      className={`icon-button ${active ? "selected" : ""} ${danger ? "danger" : ""}`}
      aria-label={label}
      title={label}
      aria-pressed={active}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export default function App({ studio }: { studio: StudioController }) {
  const { state, parsed, scripts, command, updateSettings } = studio;
  const [search, setSearch] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesLocation, setNotesLocation] = useState<'inline' | 'tab'>(() => {
    try { return localStorage.getItem('cue-studio-notes-location-v1') === 'tab' ? 'tab' : 'inline'; } catch { return 'inline'; }
  });
  const showEditorNotes = (placement = notesLocation) => {
    if (placement === 'inline') {
      setNotesOpen(true);
      requestAnimationFrame(() => document.getElementById('inline-editor-notes')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      return;
    }
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    void studio.productionNotes.prepareShare().then(url => {
      if (tab) tab.location.href = url;
      else studio.notify('Allow pop-ups to open the notes tab, or use Share notes to copy its link.');
    }).catch(e => { tab?.close(); studio.notify(e instanceof Error ? e.message : 'Could not open notes.'); });
  };
  const [section, setSection] = useState<"scripts" | "rundown">("scripts");
  const [projectFilter, setProjectFilter] = useState<ProjectFilter>(() => {
    try { return localStorage.getItem("cue-studio-project-filter-v1") || null; } catch { return null; }
  });
  useEffect(() => {
    if (projectFilter && projectFilter !== UNFILED_PROJECT && !studio.projectFolders.some(folder => folder.id === projectFilter)) setProjectFilter(null);
  }, [projectFilter, studio.projectFolders]);
  const selectProjectFilter = (id: ProjectFilter) => {
    setProjectFilter(id);
    try {
      if (id) localStorage.setItem("cue-studio-project-filter-v1", id);
      else localStorage.removeItem("cue-studio-project-filter-v1");
    } catch { /* A navigation preference never blocks access to scripts. */ }
  };
  const [batchExportOpen, setBatchExportOpen] = useState(false);
  const batchExportTrigger = useRef<HTMLButtonElement>(null);
  const appHeader = useRef<HTMLElement>(null);
  useEffect(() => {
    const header = appHeader.current;
    const shell = header?.parentElement;
    if (!header || !shell) return;
    // Reflow, zoom and responsive content can change the header's height.
    // Drawers share this anchor without rerendering the live prompting state.
    const updateInset = () =>
      shell.style.setProperty("--app-header-height", `${header.getBoundingClientRect().height}px`);
    updateInset();
    const observer = new ResizeObserver(updateInset);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);
  const [panel, setPanel] = useState<
    "controls" | "connections" | "help" | null
  >(null);
  const [controlsSection, setControlsSection] = useState<ControlSection>("reading");
  const controlsTrigger = useRef<HTMLButtonElement>(null);
  const closeControls = useCallback(() => {
    setPanel(null);
    queueMicrotask(() => controlsTrigger.current?.focus());
  }, []);
  const [editorTab, setEditorTab] = useState<"edit" | "notes">("edit");
  const [revealWord, setRevealWord] = useState<WordRevealRequest>();
  const revealRequestId = useRef(0);
  const revealClickedWord = (wordIndex: number) => {
    setEditorTab("edit");
    if (state.settings.layout === "focus") updateSettings({ layout: "studio" });
    const reveal = () =>
      setRevealWord({
        scriptId: state.script.id,
        wordIndex,
        requestId: ++revealRequestId.current,
      });
    if (document.fullscreenElement?.classList.contains("preview-desk"))
      void document.exitFullscreen().then(reveal, reveal);
    else reveal();
  };
  const [formattingTarget, setFormattingTarget] = useState<HTMLDivElement | null>(null);
  const [editingLocked, setEditingLocked] = useState(false);
  useEffect(() => { studio.setAgentEditingLocked(editingLocked); }, [editingLocked, studio.setAgentEditingLocked]);
  const [connectionTab, setConnectionTab] = useState<'devices' | 'agents'>('devices');
  const [navigatorOpen, setNavigatorOpen] = useState(false);
  const closeNavigator = useCallback(() => {
    setNavigatorOpen(false);
    document
      .querySelector<HTMLButtonElement>('[aria-label="Find in script"]')
      ?.focus();
  }, []);
  const changeHtml = useCallback(
    (html: string) => studio.updateScript({ html }),
    [studio.updateScript],
  );
  const [libraryOpen, setLibraryOpen] = useState(() => window.innerWidth > 800);
  useEffect(() => {
    const narrow = window.matchMedia("(max-width: 800px)");
    const collapseOverlay = () => {
      if (narrow.matches) setLibraryOpen(false);
    };
    narrow.addEventListener("change", collapseOverlay);
    return () => narrow.removeEventListener("change", collapseOverlay);
  }, []);
  const [notice, setNotice] = useState("");
  const { rundown, setRundown } = studio;
  const networkOriginRoom = useRef<string | null>(null);
  const [relay, setRelay] = useState("");
  const [connectingRelay, setConnectingRelay] = useState(false);
  const relayRequest = useRef(false);
  const [networkOrigin, setNetworkOrigin] = useState("");
  const [qr, setQr] = useState("");
  const [qrRole, setQrRole] = useState<ViewRole>("remote");
  const fileInput = useRef<HTMLInputElement>(null);
  const backupInput = useRef<HTMLInputElement>(null);
  const notify = (msg: string) => {
    setNotice(msg);
  };
  useEffect(() => {
    if (studio.notice) setNotice(studio.notice);
  }, [studio.notice]);
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(""), 4000);
    return () => clearTimeout(t);
  }, [notice]);
  useEffect(() => {
    document.documentElement.dataset.theme = state.settings.theme;
  }, [state.settings.theme]);
  useEffect(() => {
    if (
      studio.lanEnabled &&
      studio.networkUrls.length &&
      networkOriginRoom.current !== studio.credentials?.roomId
    ) {
      setNetworkOrigin(studio.networkUrls[0]);
      networkOriginRoom.current = studio.credentials?.roomId || null;
    }
  }, [studio.lanEnabled, studio.networkUrls, studio.credentials]);
  useEffect(() => {
    let cancelled = false;
    if (panel === "connections") {
      setQr("");
      import("qrcode")
        .then(({ default: QRCode }) =>
          QRCode.toDataURL(
            studio.getViewUrl(qrRole, networkOrigin || undefined),
            {
              width: 220,
              margin: 2,
              color: { dark: "#151719", light: "#ffffff" },
            },
          ),
        )
        .then((value) => {
          if (!cancelled) setQr(value);
        })
        .catch(() => {
          if (!cancelled) setQr("");
        });
    }
    return () => {
      cancelled = true;
    };
  }, [panel, qrRole, studio.credentials, networkOrigin]);
  const librarySearch = useMemo(() => new LibrarySearch(), []);
  const scriptFolders = useMemo(() => new Map(studio.projectFolders.flatMap(folder => folder.scriptIds.map(id => [id, folder] as const))), [studio.projectFolders]);
  const projectScripts = useMemo(() => scripts.filter(script => projectFilter === null || (projectFilter === UNFILED_PROJECT ? !scriptFolders.has(script.id) : scriptFolders.get(script.id)?.id === projectFilter)), [scripts, projectFilter, scriptFolders]);
  const activeProject = studio.projectFolders.find(folder => folder.id === projectFilter);
  const filtered = useMemo(
    () => librarySearch.search(projectScripts, search),
    [projectScripts, search, librarySearch],
  );
  const ordered = useMemo(() => {
    const byId = new Map(scripts.map((script) => [script.id, script]));
    return rundown.map((id) => byId.get(id)).filter(Boolean) as CueScript[];
  }, [scripts, rundown]);
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify("Link copied.");
    } catch {
      notify("Clipboard unavailable. Select and copy the link below.");
    }
  };
  const safe = async (action: () => unknown, message?: string) => {
    try {
      await action();
      if (message) notify(message);
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : "The action could not be completed.",
      );
    }
  };
  const connectRoom = async (address: string) => {
    if (relayRequest.current) return;
    relayRequest.current = true;
    setConnectingRelay(true);
    try {
      await safe(() => studio.connectRelay(address));
    } finally {
      relayRequest.current = false;
      setConnectingRelay(false);
    }
  };
  if (studio.role === "talent") return <TalentView studio={studio} />;
  if (studio.role === "remote") return <RemoteView studio={studio} />;
  const deviceCount = studio.status.peers.filter(
    (peer) => peer.role !== "operator",
  ).length;
  const wordCount = parsed.words.length;
  const readTime = duration((wordCount / state.settings.wpm) * 60);
  const remaining = duration(
    (Math.max(0, wordCount - state.wordIndex) / state.settings.wpm) * 60,
  );
  const move = (index: number, delta: number) =>
    setRundown((ids) => {
      const next = [...ids];
      const target = index + delta;
      if (target < 0 || target >= next.length) return ids;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  return (
    <DeferredRecoveryContext.Provider
      value={Boolean(
        state.playing ||
          state.countdownUntil ||
          studio.speechStatus.active ||
          studio.speechStatus.preparing ||
          studio.modelStatus.busy ||
          studio.audioStatus.busy ||
          studio.recording.recording ||
          studio.rehearsal.speaking,
      )}
    >
      <div
        className={`studio-shell layout-${state.settings.layout} ${libraryOpen ? "" : "library-hidden"} ${panel === "controls" ? "controls-open" : ""}`}
      >
        <header className="app-header" aria-label="Script and display controls" ref={appHeader}>
          <div className="header-identity">
            <div className="brand">
              <AudioLines size={27} aria-hidden="true" />
              <span>
                Cue<span className="brand-weight"> Studio</span>
              </span>
            </div>
            <div className="header-session">
              <span className={`status-dot ${state.playing ? "live" : ""}`} />
              {state.playing ? "Take in progress" : "Ready for your next take"}
            </div>
          </div>
          <div className="workspace-bar">
            {!libraryOpen && (
              <IconButton label="Show script library" onClick={() => setLibraryOpen(true)}>
                <PanelLeftOpen size={17} />
              </IconButton>
            )}
            <div className="workspace-script">
              <div className="script-title-row">
                <input
                  aria-label="Script title"
                  readOnly={editingLocked}
                  value={state.script.title}
                  onChange={(e) =>
                    studio.updateScript({ title: e.target.value })
                  }
                />
                <span
                  className={`saved-label ${studio.storageHealthy ? "" : "error-text"}`}
                >
                  {studio.storageHealthy ? (
                    <Check size={13} />
                  ) : (
                    <AlertTriangle size={13} />
                  )}{" "}
                  {studio.storageHealthy
                    ? "Saved locally"
                    : "Storage full · export backup"}
                </span>
              </div>
              <div className="script-details">
                <div className="workspace-context">
                  <span data-help="Total words in the current script." tabIndex={0}>{wordCount} words</span>
                  <span data-help="Estimated full-script reading time at the current words-per-minute setting." tabIndex={0}>
                    {readTime} at {state.settings.wpm} wpm
                  </span>
                </div>
                <label>
                  <input
                    aria-label="Script tags"
                    readOnly={editingLocked}
                    value={state.script.tags.join(", ")}
                    placeholder="Add tags, separated by commas"
                    onChange={(e) =>
                      studio.updateScript({
                        tags: e.target.value
                          .split(",")
                          .map((t) => t.trim())
                          .filter(Boolean),
                      })
                    }
                  />
                </label>
              </div>
            </div>
            <div className="workspace-actions">
              <button data-help="Open connection links and QR codes for talent screens and phone controllers."
                className="subtle-button"
                onClick={() =>
                  setPanel(panel === "connections" ? null : "connections")
                }
              >
                <Smartphone size={15} />
                Connect devices<span className="peer-count" data-help="Number of connected talent displays and remote controllers.">{deviceCount}</span>
              </button>
              <button
                ref={controlsTrigger}
                data-help="Open settings and studio tools on the left. Adjust the script while watching Talent view live."
                className={`subtle-button ${panel === "controls" ? "active" : ""}`}
                aria-expanded={panel === "controls"}
                aria-controls="studio-controls-panel"
                onClick={() => panel === "controls" ? closeControls() : setPanel("controls")}
              >
                <Settings2 size={15} />
                Settings & tools
              </button>
              <button data-help={studio.credentials ? "Open a synchronized talent window that you can move to the teleprompter screen." : "The display link is being prepared. This button becomes available when the room is ready."}
                className="primary-button"
                aria-label="Open talent display"
                disabled={!studio.credentials}
                onClick={() => studio.openOutput()}
              >
                <MonitorUp size={16} />
                {studio.credentials ? "Open display" : "Preparing display…"}
              </button>
            </div>
          </div>
          <div className="header-actions">
            <span className="local-label">
              Local-first <span className="status-dot" />
            </span>
            <IconButton
              label={
                state.settings.theme === "dark"
                  ? "Switch to light theme"
                  : "Switch to dark theme"
              }
              onClick={() =>
                updateSettings({
                  theme: state.settings.theme === "dark" ? "light" : "dark",
                })
              }
            >
              {state.settings.theme === "dark" ? (
                <Sun size={17} />
              ) : (
                <Moon size={17} />
              )}
            </IconButton>
            <IconButton
              label="Help and keyboard shortcuts"
              active={panel === "help"}
              onClick={() => setPanel(panel === "help" ? null : "help")}
            >
              <CircleHelp size={18} />
            </IconButton>
          </div>
        </header>
        <aside className="library">
          <div className="library-heading">
            <h2>Your workspace</h2>
            <IconButton
              label="Collapse script library"
              onClick={() => setLibraryOpen(false)}
            >
              <PanelLeftClose size={16} />
            </IconButton>
          </div>
          <div className="library-switch">
            <button data-help="Show the script library. The count is the number of scripts saved in this workspace."
              className={section === "scripts" ? "active" : ""}
              onClick={() => { setSection("scripts"); selectProjectFilter(null); }}
            >
              <BookOpen size={16} />
              Scripts<span>{scripts.length}</span>
            </button>
            <button data-help="Show the ordered list of scripts for your shoot. The count includes every rundown entry."
              className={section === "rundown" ? "active" : ""}
              onClick={() => setSection("rundown")}
            >
              <ListVideo size={16} />
              Rundown<span>{ordered.length}</span>
            </button>
          </div>
          <button data-help={activeProject ? `Create and open a script in ${activeProject.name}.` : "Create a new script in this browser workspace and open it for editing."} className="new-script" onClick={() => studio.createScript(activeProject?.id)}>
            <Plus size={17} />
            New script<span>⌘</span>
          </button>
          {section === "scripts" && <ProjectFolders studio={studio} selected={projectFilter} onSelect={selectProjectFilter} />}
          <label className="library-search">
            <Search size={15} />
            <input
              aria-label="Search scripts"
              placeholder={activeProject ? "Search this project" : "Search titles, tags or text"}
              data-help="Find scripts by title, tags or words inside the current project. Choose All scripts to search the whole workspace."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <div className="visually-hidden" role="status">
            {section === "scripts" && search.trim()
              ? `${filtered.length} matching ${filtered.length === 1 ? "script" : "scripts"}.`
              : ""}
          </div>
          <div className="library-list">
            {section === "scripts" &&
              filtered.map(({ script, preview }) => (
                <div
                  className={`script-item ${script.id === state.script.id ? "active" : ""}`}
                  key={script.id}
                >
                  <button data-help="Load this script into the editor and connected talent displays."
                    className="script-select"
                    onClick={() => studio.selectScript(script.id)}
                  >
                    <FileText size={16} />
                    <span>
                      <strong>{script.title || "Untitled script"}</strong>
                      <small>
                        {[scriptFolders.get(script.id)?.name, ...script.tags].filter(Boolean).join(" · ") || "Unfiled"}{" "}
                        ·{" "}
                        {new Date(script.updatedAt).toLocaleDateString(
                          undefined,
                          { month: "short", day: "numeric" },
                        )}
                      </small>
                      {preview && (
                        <span className="script-search-preview">
                          {preview.before}<mark>{preview.match}</mark>{preview.after}
                        </span>
                      )}
                    </span>
                  </button>
                  <div className="script-actions">
                    <IconButton
                      label={`Duplicate ${script.title}`}
                      onClick={() => studio.duplicateScript(script.id)}
                    >
                      <Copy size={13} />
                    </IconButton>
                    <IconButton
                      label={`Add ${script.title} to rundown`}
                      onClick={() => {
                        setRundown((ids) => [...ids, script.id]);
                        notify("Script added to rundown.");
                      }}
                    >
                      <ListVideo size={13} />
                    </IconButton>
                    <ScriptExportMenu
                      title={script.title}
                      onExport={(format) => { void safe(() => studio.exportScript(format, script.id)); }}
                    />
                    <ScriptFolderPicker title={script.title} folders={studio.projectFolders} folderId={scriptFolders.get(script.id)?.id || null} onMove={folderId => studio.moveScriptToFolder(script.id, folderId)} />
                    <IconButton
                      label={`Delete ${script.title}`}
                      danger
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete “${script.title}”? You can restore it later from Settings & tools → Recovery & updates.`,
                          )
                        )
                          studio.deleteScript(script.id);
                      }}
                    >
                      <Trash2 size={13} />
                    </IconButton>
                  </div>
                </div>
              ))}
            {section === "scripts" && !filtered.length && (
              <div className="library-empty">{search.trim() ? "No scripts match your search." : activeProject ? "This project is empty. Create a script, import files, or move a script here." : "No unfiled scripts. Choose All scripts to see the whole library."}</div>
            )}
            {section === "rundown" &&
              ordered.map((script, index) => (
                <div
                  className={`rundown-item ${script.id === state.script.id ? "active" : ""}`}
                  key={`${script.id}-${index}`}
                >
                  <span className="rundown-number" data-help="Position of this script in the shoot order." tabIndex={0}>
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <button data-help="Load this rundown script into the editor and connected talent displays." onClick={() => studio.selectScript(script.id)}>
                    {script.title}
                  </button>
                  <div>
                    <IconButton
                      label={`Move ${script.title} earlier`}
                      onClick={() => move(index, -1)}
                    >
                      <ArrowUp size={12} />
                    </IconButton>
                    <IconButton
                      label={`Move ${script.title} later`}
                      onClick={() => move(index, 1)}
                    >
                      <ArrowDown size={12} />
                    </IconButton>
                    <IconButton
                      label={`Remove ${script.title} from rundown`}
                      onClick={() =>
                        setRundown((ids) => ids.filter((_, i) => i !== index))
                      }
                    >
                      <X size={12} />
                    </IconButton>
                  </div>
                </div>
              ))}
            {section === "rundown" && !ordered.length && (
              <div className="library-empty">
                <ListVideo size={28} />
                <strong>Build your shoot order</strong>
                <p>
                  Add scripts from the library, then move them into the order
                  you need.
                </p>
              </div>
            )}
          </div>
          <div className="library-bottom">
            <button data-help="Choose script files to add to your library." onClick={() => fileInput.current?.click()}>
              <Upload size={15} />
              Import scripts
            </button>
            <button ref={batchExportTrigger} type="button" data-help="Select scripts and download them as plain text or formatted Word documents. Multiple scripts download together in a ZIP."
              onClick={() => setBatchExportOpen(true)}>
              <Download size={15} />
              Batch export
            </button>
            <button data-help="Download a workspace backup containing your scripts, saved versions and recovery data."
              onClick={() =>
                safe(() => studio.exportBackup(), "Workspace backup exported.")
              }
            >
              <Download size={15} />
              Back up workspace
            </button>
            <button data-help="Choose a workspace backup file to restore its saved data." onClick={() => backupInput.current?.click()}>
              <RefreshCw size={15} />
              Restore backup
            </button>
            <p>
              Saved on this device.
              <br />
              Free and open source.
            </p>
          </div>
        </aside>
        <main className="main-workspace">
          <header className="workspace-header" aria-label="Script editing controls">
            <div className="workspace-editing-header">
              <div className="script-tabs">
                <div>
                  <button data-help="Open the script editor to change the text shown on talent displays."
                    className={editorTab === "edit" ? "active" : ""}
                    onClick={() => {
                      setRevealWord(undefined);
                      setEditorTab("edit");
                    }}
                  >
                    Script
                  </button>
                  <button data-help="Open private production notes for this script. Notes do not appear on talent displays."
                    className={editorTab === "notes" ? "active" : ""}
                    onClick={() => {
                      setRevealWord(undefined);
                      setEditorTab("notes");
                    }}
                  >
                    Production notes
                  </button>
                  <IconButton
                    label="Find in script"
                    active={navigatorOpen}
                    onClick={() => setNavigatorOpen(!navigatorOpen)}
                  >
                    <Search size={15} />
                  </IconButton>
                  <IconButton
                    label={editingLocked ? "Unlock editing" : "Lock editing"}
                    active={editingLocked}
                    onClick={() => setEditingLocked(!editingLocked)}
                  >
                    {editingLocked ? (
                      <LockKeyhole size={15} />
                    ) : (
                      <LockKeyholeOpen size={15} />
                    )}
                  </IconButton>
                </div>
              <div className="editor-notes-launcher" data-production-notes>
                <button data-help="Log talent, rate takes and prepare a handoff for the editor." onClick={() => showEditorNotes()}><FileText size={18} />Editor notes</button>
                <label>Open in<select aria-label="Notes location" value={notesLocation} onChange={e => {
                  const next = e.target.value as 'inline' | 'tab'; setNotesLocation(next);
                  try { localStorage.setItem('cue-studio-notes-location-v1', next); } catch { studio.notify('The notes location preference could not be saved on this device.'); }
                  if (next === 'tab') setNotesOpen(false);
                }}><option value="inline">Below workspace</option><option value="tab">Separate tab</option></select></label>
              </div>
                <select
                  aria-label="Export script format"
                  data-help="Download this script as formatted HTML, plain text, or editable JSON. Plain text does not preserve formatting."
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value)
                      safe(() =>
                        studio.exportScript(
                          e.target.value as "html" | "txt" | "json",
                        ),
                      );
                    e.target.value = "";
                  }}
                >
                  <option value="" disabled>
                    Export script
                  </option>
                  <option value="html">Rich text HTML</option>
                  <option value="txt">Plain text</option>
                  <option value="json">Editable JSON</option>
                </select>
              </div>
              <div className="workspace-formatting-row">
                <div className="workspace-formatting" ref={setFormattingTarget} />
                <div className="preview-heading preview-heading-header">
                  <div className="preview-title"><h2>Talent view</h2><ListeningIndicator studio={studio} /></div>
                  <RehearsalControls studio={studio} />
                  <span>Live operator mirror</span>
                  <IconButton
                    label="Fullscreen operator preview"
                    onClick={() =>
                      document.querySelector(".preview-desk")?.requestFullscreen()
                    }
                  >
                    <Maximize size={15} />
                  </IconButton>
                </div>
              </div>
              <ScriptNavigator
                key={state.script.id}
                parsed={parsed}
                wordIndex={state.wordIndex}
                command={command}
                open={navigatorOpen}
                onClose={closeNavigator}
              />
            </div>
          </header>
          <div className={`desk-content ${notesOpen ? "with-editor-notes" : ""}`}>
            <section className="script-desk" aria-label="Script editor">
              {editorTab === "edit" ? (
                <RichEditor
                  key={state.script.id}
                  id={state.script.id}
                  html={state.script.html}
                  onChange={changeHtml}
                  locked={editingLocked}
                  revealWord={revealWord}
                  toolbarTarget={formattingTarget}
                />
              ) : (
                <div className="notes-editor">
                  <label htmlFor="production-notes">
                    Notes for this script
                  </label>
                  <textarea
                    id="production-notes"
                    readOnly={editingLocked}
                    placeholder="Pronunciation, camera setup, delivery notes, alternate takes…"
                    value={state.script.notes}
                    onChange={(e) =>
                      studio.updateScript({ notes: e.target.value })
                    }
                  />
                  <p>
                    Notes stay on the operator desk and never appear on the
                    talent display.
                  </p>
                </div>
              )}
              <div className="cue-strip">
                <div>
                  <Flag size={14} />
                  <span>Cue points</span>
                </div>
                {parsed.markers.length ? (
                  parsed.markers.map((m, i) => (
                    <button data-help="Move every display to this cue in the script."
                      key={i}
                      onClick={() =>
                        command({ type: "seek", wordIndex: m.wordIndex })
                      }
                    >
                      {m.title}
                      <small data-help="This cue’s word index in the script, counted from zero.">{m.wordIndex}</small>
                    </button>
                  ))
                ) : (
                  <p>Add a cue with the flag in the editor toolbar.</p>
                )}
              </div>
            </section>
            <section className="preview-desk" aria-label="Operator preview">
              <div className="preview-heading preview-heading-inline">
                <div className="preview-title"><h2>Talent view</h2><ListeningIndicator studio={studio} /></div>
                <RehearsalControls studio={studio} />
                <span>Live operator mirror</span>
                <IconButton
                  label="Fullscreen operator preview"
                  onClick={() =>
                    document.querySelector(".preview-desk")?.requestFullscreen()
                  }
                >
                  <Maximize size={15} />
                </IconButton>
              </div>
              <div className="preview-screen">
                <PromptSurface
                  state={state}
                  parsed={parsed}
                  onSeek={(wordIndex) => command({ type: "seek", wordIndex })}
                  onWordClick={revealClickedWord}
                  compact={state.settings.layout === "compact"}
                />
                <div className="preview-screen-label">
                  <span
                    className={`status-dot ${state.playing ? "live" : ""}`}
                  />
                  {state.blackout
                    ? "BLACKOUT"
                    : state.playing
                      ? "PROMPTING"
                      : "STANDBY"}
                  <span data-help="Current reading word and total script word count, shared with the talent displays." tabIndex={0}>
                    Word {Math.min(wordCount, state.wordIndex + 1)} /{" "}
                    {wordCount}
                  </span>
                </div>
              </div>
              <div className="preview-metrics">
                <div>
                  <span>Elapsed</span>
                  <strong data-help="Elapsed prompting time for the current take." tabIndex={0}>{duration(state.elapsed)}</strong>
                </div>
                <div>
                  <span>Remaining estimate</span>
                  <strong data-help="Estimated time left from the remaining words at the current pace. Speech following and pauses can change the actual time." tabIndex={0}>{remaining}</strong>
                </div>
                <div>
                  <span>Speed</span>
                  <strong data-help="Current pace in words per minute. Constant and sound-gated modes use this speed." tabIndex={0}>
                    {state.settings.wpm}
                    <small>wpm</small>
                  </strong>
                </div>
              </div>
              <Launchpad studio={studio} />

            </section>
            {notesOpen && <section id="inline-editor-notes" className="inline-editor-notes" data-production-notes>
              <div className="inline-notes-heading"><button className="subtle-button" onClick={() => setNotesOpen(false)}><X size={15} />Close editor notes</button></div>
              <ProductionNotes controller={studio.productionNotes} currentScriptId={state.script.id} />
            </section>}
          </div>
          <div className="transport-bar">
            <ReadingPosition
              position={state.wordIndex + state.wordProgress}
              length={wordCount}
              onSeek={studio.scrubTo}
              onSpeedStep={(direction) => command({ type: "speed", wpm: state.settings.wpm + direction * (state.settings.speedStep ?? 10) })}
            />
            <div className="transport-main">
              <IconButton
                label="Restart script"
                onClick={() => command({ type: "reset" })}
              >
                <SkipBack size={20} />
              </IconButton>
              <IconButton
                label="Back 5 words"
                onClick={() => command({ type: "nudge", words: -5 })}
              >
                <ChevronLeft size={22} />
              </IconButton>
              <button data-help={state.countdownUntil ? "Cancel the countdown before prompting begins. Shortcut: Space." : state.playing ? "Pause prompting at the current reading position. Shortcut: Space." : "Start a take from the current reading position using the selected scroll mode and countdown. Shortcut: Space."}
                className={`play-button ${state.playing ? "playing" : ""}`}
                onClick={() => command({ type: "toggle" })}
                aria-label={
                  state.countdownUntil
                    ? "Cancel countdown"
                    : state.playing
                      ? "Pause prompting"
                      : "Start prompting"
                }
              >
                {state.playing ? (
                  <Pause size={21} fill="currentColor" />
                ) : (
                  <Play size={21} fill="currentColor" />
                )}
                <span>
                  {state.countdownUntil
                    ? "Cancel countdown"
                    : state.playing
                      ? "Pause"
                      : "Start take"}
                </span>
                <kbd>Space</kbd>
              </button>
              <IconButton
                label="Forward 5 words"
                onClick={() => command({ type: "nudge", words: 5 })}
              >
                <ChevronRight size={22} />
              </IconButton>
            </div>
            <label className="transport-mode">
              <span>Scroll</span>
              <select
                aria-label="Scroll mode"
                data-help="Choose a scroll mode. Voice tracking and Sound gated start listening automatically; other modes switch the microphone off."
                value={state.settings.mode}
                onChange={(e) =>
                  void studio.selectScrollMode(e.target.value as typeof state.settings.mode)
                }
              >
                <option value="manual">Manual</option>
                <option value="constant">Constant speed</option>
                <option value="timed">Timed duration</option>
                <option value="voice">Voice tracking</option>
                <option value="sound">Sound gated</option>
              </select>
            </label>
            <label className="transport-speed">
              <span>Pace</span>
              <DragNumberInput
                aria-label="Words per minute"
                data-help={`Set the reading pace from 10 to 600 words per minute. Each adjustment step changes it by ${state.settings.speedStep ?? 10} words per minute.`}
                min={10}
                max={600}
                step={state.settings.speedStep ?? 10}
                value={state.settings.wpm}
                onValueChange={(wpm) =>
                  command({ type: "speed", wpm })
                }
              />
              <span>wpm</span>
            </label>
            <div className="transport-quick-settings" role="group" aria-label="Playback options">
              <label className="transport-option">
                <span>Arrow-key speed step</span>
                <span className="transport-option-value">
                  <DragNumberInput
                    aria-label="Arrow-key speed step"
                    data-help="Words per minute added or removed by each Up or Down shortcut and Pace adjustment. Drag horizontally or type a value from 1 to 100."
                    min={1}
                    max={100}
                    step={1}
                    value={state.settings.speedStep ?? 10}
                    onValueChange={(speedStep) => command({ type: "settings", settings: { speedStep: Math.max(1, Math.min(100, Math.round(speedStep))) } })}
                  />
                  <span>wpm</span>
                </span>
              </label>
              <label className="transport-option">
                <span>Start countdown</span>
                <span className="transport-option-value">
                  <DragNumberInput
                    aria-label="Start countdown"
                    data-help="Seconds to wait after Start take before scrolling begins. Zero starts immediately. Changes apply to your next start. Drag horizontally or type a value from 0 to 15."
                    min={0}
                    max={15}
                    step={1}
                    value={state.settings.countdown}
                    onValueChange={(countdown) => command({ type: "settings", settings: { countdown: Math.max(0, Math.min(15, Math.round(countdown))) } })}
                  />
                  <span>sec</span>
                </span>
              </label>
            </div>
          </div>
          <footer className="desk-status">
            <span>
              <span
                className={`status-dot ${studio.status.connected ? "" : "disconnected"}`}
              />
              {!studio.status.connected
                ? "Output connection offline"
                : studio.status.kind === "relay"
                  ? "Live relay connected"
                  : "Same-device sync ready"}
            </span>
            <span data-help="Connected talent and controller count, followed by measured relay latency in milliseconds when available." tabIndex={0}>
              {deviceCount} connected device{deviceCount === 1 ? "" : "s"}
              {studio.status.latency !== null
                ? ` · ${studio.status.latency} ms`
                : ""}
            </span>
            <span>
              Keyboard ready ·{" "}
              {studio.storageHealthy
                ? "Saved on this device"
                : "Storage full · export a backup"}
            </span>
            <nav className="information-links" aria-label="Service information">
              <a className="ai-guide-link" href="/ai-guide.html" target="_blank" rel="noopener noreferrer" data-help="Open the AI writing guide in a separate tab: supported formatting, cue syntax, examples and a prompt to give your AI.">AI writing guide</a>
              <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy</a>
              <a href="/legal.html" target="_blank" rel="noopener noreferrer">Legal</a>
              <a href="/source.html" target="_blank" rel="noopener noreferrer">Source</a>
            </nav>
          </footer>
        </main>
        {panel === "controls" && (
          <StudioControls
            studio={studio}
            section={controlsSection}
            onSectionChange={setControlsSection}
            onClose={closeControls}
          />
        )}
        {panel && panel !== "controls" && (
          <aside className="utility-panel">
            <div className="utility-heading">
              <h2>
                {panel === "connections" ? "Connect your set" : "Studio guide"}
              </h2>
              <IconButton
                label="Close side panel"
                onClick={() => setPanel(null)}
              >
                <X size={20} />
              </IconButton>
            </div>
            {panel === 'connections' && <div className="connection-tabs" role="tablist" aria-label="Connection type" onKeyDown={event => {
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const next = event.key === 'Home' ? 'devices' : event.key === 'End' ? 'agents' : connectionTab === 'agents' ? 'devices' : 'agents';
              setConnectionTab(next);
              document.getElementById(next === 'agents' ? 'agent-connections-tab' : 'device-connections-tab')?.focus();
            }}>
              <button id="device-connections-tab" role="tab" tabIndex={connectionTab === 'devices' ? 0 : -1} aria-selected={connectionTab === 'devices'} aria-controls={connectionTab === 'devices' ? 'device-connections-panel' : undefined} onClick={() => setConnectionTab('devices')}>Screens & remotes</button>
              <button id="agent-connections-tab" role="tab" tabIndex={connectionTab === 'agents' ? 0 : -1} aria-selected={connectionTab === 'agents'} aria-controls={connectionTab === 'agents' ? 'agent-connections-panel' : undefined} onClick={() => setConnectionTab('agents')}>AI models · MCP</button>
            </div>}
            {panel === 'connections' && connectionTab === 'agents' && <div id="agent-connections-panel" role="tabpanel" aria-labelledby="agent-connections-tab"><AgentConnections studio={studio} /></div>}
            {panel === "connections" && connectionTab === 'devices' && (
              <div id="device-connections-panel" role="tabpanel" aria-labelledby="device-connections-tab" className="connections-panel">
                <h3>One script. Every screen.</h3>
                <p>
                  Open a talent display on this computer, or share a private link
                  with your talent display or phone.
                </p>
                <button data-help={studio.credentials ? "Open a synchronized talent window for your teleprompter screen." : "Wait for the room link to finish preparing before opening a display."}
                  className="primary-button full-width"
                  disabled={!studio.credentials}
                  onClick={() => studio.openOutput()}
                >
                  <MonitorUp size={17} />
                  {studio.credentials
                    ? "Open a clean talent window"
                    : "Preparing display…"}
                </button>
                <div className="connection-state">
                  <Wifi size={19} />
                  <span>
                    <strong>
                      {studio.status.kind === "relay"
                        ? "Live relay room"
                        : "Same-computer room"}
                    </strong>
                    <small>
                      {studio.status.kind === "relay"
                        ? studio.status.connected
                          ? "Ready to share. Keep this operator tab open."
                          : studio.status.error === "Authentication failed"
                            ? "This room expired. Start a new room below."
                            : "Connection interrupted. Your script stays saved on this device."
                        : "Displays in this browser can join. Start a live room for other devices."}
                    </small>
                  </span>
                </div>
                <button data-help={connectingRelay ? "A room connection is already in progress. Wait for it to finish." : "Create a fresh live room and new sharing links while keeping the current script. Reconnect devices with the new links."}
                  className="subtle-button full-width"
                  disabled={connectingRelay}
                  onClick={() => connectRoom("")}
                >
                  <RefreshCw size={16} />
                  {connectingRelay ? "Connecting…" : "Start a new room"}
                </button>
                <p className="setting-help">
                  Starting a new room preserves your script and creates fresh
                  connection links. Share the new links with your devices.
                </p>
                <details>
                  <summary data-help="Enter the address of a relay you host yourself instead of using the default live relay.">Use your own relay</summary>
                  <label className="setting-field">
                    <span>Relay address</span>
                    <input
                      aria-label="Local relay address"
                      placeholder="http://192.168.1.10:4317"
                      value={relay}
                      onChange={(e) => setRelay(e.target.value)}
                    />
                  </label>
                  <button data-help={connectingRelay ? "A relay connection is already in progress." : "Connect this operator desk to the relay address above. Devices must use the resulting room links."}
                    className="subtle-button full-width"
                    disabled={connectingRelay}
                    onClick={() => connectRoom(relay)}
                  >
                    <Radio size={16} />
                    Connect relay
                  </button>
                  <p className="setting-help">
                    Connect to a relay you host yourself using its HTTPS or local
                    network address. Self-hosting instructions are in the source
                    documentation. Keep this operator tab open.
                  </p>
                </details>
                <label className="setting-field">
                  <span>Link address</span>
                  <select
                    aria-label="Network link address"
                data-help="Choose the server address to include in links and QR codes shared with other devices."
                    value={networkOrigin}
                    onChange={(e) => setNetworkOrigin(e.target.value)}
                  >
                    <option value="">This browser address</option>
                    {studio.networkUrls.map((url) => (
                      <option key={url} value={url}>
                        {url}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="segmented">
                  <button data-help="Show a link and QR code for a phone that can control the room."
                    aria-pressed={qrRole === "remote"}
                    onClick={() => setQrRole("remote")}
                  >
                    <Smartphone size={16} />
                    Phone control
                  </button>
                  <button data-help="Show a read-only talent-display link and QR code for another screen."
                    aria-pressed={qrRole === "talent"}
                    onClick={() => setQrRole("talent")}
                  >
                    <MonitorUp size={16} />
                    Talent view
                  </button>
                </div>
                {qr && (
                  <img
                    className="connection-qr"
                    src={qr}
                    alt={`QR code to open ${qrRole === "remote" ? "phone remote" : "talent display"}`}
                  />
                )}
                <label className="setting-field">
                  <span>
                    {qrRole === "remote"
                      ? "Private controller link"
                      : "Read-only talent link"}
                  </span>
                  <input
                    aria-label="Device connection link"
                    readOnly
                    value={studio.getViewUrl(
                      qrRole,
                      networkOrigin || undefined,
                    )}
                    onFocus={(e) => e.target.select()}
                  />
                </label>
                <button data-help={qrRole === "remote" ? "Copy the private controller link. Anyone with this link can control the room." : "Copy the read-only talent link to open the synchronized script on another screen."}
                  className="subtle-button full-width"
                  onClick={() =>
                    copy(studio.getViewUrl(qrRole, networkOrigin || undefined))
                  }
                >
                  <Copy size={16} />
                  Copy {qrRole === "remote" ? "controller" : "talent"} link
                </button>
                <p className="setting-help">
                  Controller links can operate this room. Share them only with
                  your crew. Same-device links work in tabs on this computer; a
                  phone needs a live relay. The public edition connects devices
                  over the internet; local editions can use a trusted LAN.
                </p>
                <h4>Connected devices</h4>
                {studio.status.peers.length ? (
                  studio.status.peers.map((peer) => (
                    <div className="peer-row" key={peer.id}>
                      {peer.role === "remote" ? (
                        <Smartphone size={16} />
                      ) : (
                        <MonitorUp size={16} />
                      )}
                      <span>
                        {peer.name}
                        <small>{peer.role}</small>
                      </span>
                      <span className="status-dot" />
                    </div>
                  ))
                ) : (
                  <p className="setting-help">Open an output to see it here.</p>
                )}
                {studio.status.error && (
                  <p role="alert" className="error-text">
                    {studio.status.error}
                  </p>
                )}
              </div>
            )}
            {panel === "help" && (
              <div className="help-panel">
                <h3>Write with an AI</h3>
                <p>Give your AI the formatting guide to prepare a script with highlights, word sizes and cue markers that import correctly.</p>
                <a className="ai-guide-link" href="/ai-guide.html" target="_blank" rel="noopener noreferrer">Open AI writing guide</a>
                <p>To let an assistant work directly in this workspace, connect it through MCP.</p>
                <button className="subtle-button" onClick={() => { setConnectionTab('agents'); setPanel('connections'); }}>Connect an AI model</button>
                <h3>A calm first take</h3>
                <ol>
                  <li>
                    Write or import your script. Select words to add emphasis or
                    color.
                  </li>
                  <li>
                    Open the talent display and move it to the Elgato screen.
                    Enter fullscreen from that window.
                  </li>
                  <li>
                    Choose Voice tracking or Sound gated to start listening.
                    Check the microphone indicator beside Talent view; click it
                    to stop, cancel preparation, or retry.
                  </li>
                  <li>
                    Press Start take. Click a preview word or use the arrow keys
                    to take over.
                  </li>
                </ol>
                <h3>Number fields</h3>
                <p>Drag any number field left or right to adjust it live. Click to type a value, or use the arrow keys while the field is focused.</p>
                <h3>Script editing shortcuts</h3>
                <dl>
                  <dt>Enter</dt>
                  <dd>New line with normal line spacing</dd>
                  <dt>Shift+Enter</dt>
                  <dd>New paragraph with extra spacing</dd>
                </dl>
                <h3>Operator shortcuts</h3>
                <dl>
                  <dt>Space</dt>
                  <dd>Play or pause</dd>
                  <dt>← / →</dt>
                  <dd>Back or forward 5 words</dd>
                  <dt>↑ / ↓</dt>
                  <dd>Faster or slower by {state.settings.speedStep ?? 10} wpm. Adjust the step in Settings & tools → Playback.</dd>
                  <dt>Home</dt>
                  <dd>Return to script start</dd>
                  <dt>B</dt>
                  <dd>Blackout / restore talent</dd>
                </dl>
                <h3>Your data stays yours</h3>
                <p>
                  Your script library is stored in this browser. On the public
                  site, the current script also goes to the live-sync server
                  while this operator tab is open. Local Whisper processes
                  audio on your device; optional browser speech may use its
                  provider's servers. Export a backup before clearing browser
                  data.
                </p>
                <nav className="information-links" aria-label="Privacy and licensing">
                  <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy details</a>
                  <a href="/legal.html" target="_blank" rel="noopener noreferrer">Legal notice</a>
                  <a href="/source.html" target="_blank" rel="noopener noreferrer">Open source · MIT</a>
                </nav>
                <h3>Elgato setup</h3>
                <p>
                  Use Display Mode with an extended desktop. Elgato applies the
                  optical flip itself; software mirroring should normally be
                  off. Choose Original or XL in Settings & tools → Talent display for a starting
                  layout.
                </p>
                <h3>Cues and handover</h3>
                <p>
                  Put <code>[cue: Camera two]</code> on its own line. Cue points
                  let the operator jump to a section without becoming part of
                  speech matching. Manual seeking temporarily takes priority
                  over speech.
                </p>
              </div>
            )}
          </aside>
        )}
        {batchExportOpen && <BatchExportDialog scripts={scripts} onExport={studio.exportBatch} returnFocus={batchExportTrigger.current} onClose={() => setBatchExportOpen(false)} />}
        {notice && (
          <div className="toast" role="status">
            <Check size={16} />
            {notice}
            <button data-help="Dismiss this notification without changing the current script or take."
              aria-label="Dismiss notification"
              onClick={() => setNotice("")}
            >
              <X size={14} />
            </button>
          </div>
        )}
        <input
          ref={fileInput}
          className="visually-hidden"
          type="file"
          multiple
          accept=".txt,.html,.htm,.md,.markdown,.json,.docx,.srt,.vtt"
          aria-label="Import script files"
          onChange={(e) => {
            safe(() => studio.importScripts(Array.from(e.target.files || []), activeProject?.id));
            e.target.value = "";
          }}
        />
        <input
          ref={backupInput}
          className="visually-hidden"
          type="file"
          accept=".json"
          aria-label="Restore workspace file"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) safe(() => studio.importBackup(f));
            e.target.value = "";
          }}
        />
      </div>
    </DeferredRecoveryContext.Provider>
  );
}

function operatorIsConnected(studio: StudioController) {
  return (
    studio.status.connected &&
    studio.status.peers.some((peer) => peer.role === "operator")
  );
}
function TalentView({ studio }: { studio: StudioController }) {
  // A read-only talent peer can orient its own screen without broadcasting a
  // settings mutation or restarting the prompt's motion driver. Untouched axes
  // follow the operator; an override lasts until this view is reloaded.
  const [localOrientation, setLocalOrientation] = useState<{ flipX?: boolean; flipY?: boolean }>({});
  const flipX = localOrientation.flipX ?? studio.state.settings.flipX;
  const flipY = localOrientation.flipY ?? studio.state.settings.flipY;
  if (!studio.hasSnapshot) return <WaitingView studio={studio} orientation={{ flipX, flipY }} />;
  const connected = operatorIsConnected(studio);
  return (
    <main className="talent-page" data-flip-x={flipX} data-flip-y={flipY} style={{ transform: `scale(${flipX ? -1 : 1},${flipY ? -1 : 1})` }}>
      <PromptSurface state={studio.state} parsed={studio.parsed} talent />
      {!connected && (
        <div className="talent-connection-warning" role="status">
          <Wifi size={17} />
          <span>
            <strong>Operator disconnected · holding position</strong>
            <small>
              Keep this window open. The display will reconnect automatically.
            </small>
          </span>
        </div>
      )}
      <TalentControls
        connected={connected}
        title={studio.state.script.title}
        flipX={flipX}
        flipY={flipY}
        onMirror={() => setLocalOrientation((current) => ({ ...current, flipX: !(current.flipX ?? studio.state.settings.flipX) }))}
        onFlip={() => setLocalOrientation((current) => ({ ...current, flipY: !(current.flipY ?? studio.state.settings.flipY) }))}
      />
    </main>
  );
}
function RemoteView({ studio }: { studio: StudioController }) {
  const { state, parsed } = studio;
  const connected = operatorIsConnected(studio);
  const command: StudioController["command"] = (cmd) => {
    if (connected) studio.command(cmd);
  };
  const [text, setText] = useState("Please hold");
  if (!studio.hasSnapshot) return <WaitingView studio={studio} />;
  return (
    <main className="remote-page">
      <header>
        <div className="brand">
          <AudioLines size={26} />
          <span>Cue Studio</span>
        </div>
        <span className={`remote-status ${connected ? "" : "offline"}`}>
          <span className="status-dot" />
          {connected ? "Connected" : "Offline"}
        </span>
      </header>
      {!connected && (
        <p className="remote-connection-warning" role="status">
          Waiting for the operator. Controls will resume automatically when the
          connection returns.
        </p>
      )}
      <div className="remote-script">
        <span>Controlling</span>
        <h1>{state.script.title}</h1>
        <div>
          <span data-help="Current reading word and total word count reported by the operator." tabIndex={0}>
            Word {state.wordIndex + 1} / {parsed.words.length}
          </span>
          <strong data-help="Elapsed prompting time for the current take." tabIndex={0}>{duration(state.elapsed)}</strong>
        </div>
      </div>
      <div className="remote-preview">
        <PromptSurface
          state={state}
          parsed={parsed}
          compact
          onSeek={(wordIndex) => command({ type: "seek", wordIndex })}
        />
      </div>
      <input
        className="remote-position"
        aria-label="Remote reading position"
        data-help="Drag to move the shared reading position through the script while connected to the operator."
        type="range"
        min={0}
        max={Math.max(0, parsed.words.length - 1)}
        value={state.wordIndex}
        onChange={(e) =>
          command({ type: "seek", wordIndex: Number(e.target.value) })
        }
      />
      <div className="remote-transport">
        <button data-help="Move the connected displays back five words."
          aria-label="Remote back 5 words"
          onClick={() => command({ type: "nudge", words: -5 })}
        >
          <ChevronLeft size={30} />
        </button>
        <button data-help={!connected ? "Reconnect to the operator before controlling the take." : state.countdownUntil ? "Cancel the countdown before prompting starts." : state.playing ? "Pause prompting at the current position." : "Start prompting from the current position using the operator’s selected mode."}
          className="play-button"
          onClick={() => command({ type: "toggle" })}
        >
          {state.playing ? <Pause size={28} /> : <Play size={28} />}{" "}
          {state.playing ? "Pause" : "Start"}
        </button>
        <button data-help="Move the connected displays forward five words."
          aria-label="Remote forward 5 words"
          onClick={() => command({ type: "nudge", words: 5 })}
        >
          <ChevronRight size={30} />
        </button>
      </div>
      <div className="remote-speed">
        <button data-help="Reduce the reading pace by 10 words per minute, down to the minimum of 10."
          aria-label="Decrease speed"
          onClick={() =>
            command({ type: "speed", wpm: state.settings.wpm - 10 })
          }
        >
          −10
        </button>
        <span>
          <strong data-help="Current reading pace in words per minute, reported by the operator." tabIndex={0}>{state.settings.wpm}</strong>words per minute
        </span>
        <button data-help="Increase the reading pace by 10 words per minute, up to the maximum of 600."
          aria-label="Increase speed"
          onClick={() =>
            command({ type: "speed", wpm: state.settings.wpm + 10 })
          }
        >
          +10
        </button>
      </div>
      <div className="remote-safety">
        <button data-help="Return to the beginning of the script and reset the take timer." onClick={() => command({ type: "reset" })}>
          <SkipBack size={19} />
          Restart
        </button>
        <button data-help={state.blackout ? "Show the script on talent displays again." : "Cover talent displays with black without resetting their reading position."}
          className={state.blackout ? "blackout-active" : ""}
          onClick={() => command({ type: "blackout" })}
        >
          <EyeOff size={19} />
          {state.blackout ? "Restore" : "Blackout"}
        </button>
      </div>
      {parsed.markers.length > 0 && (
        <section className="remote-cues">
          <h2>Cue points</h2>
          {parsed.markers.map((m, i) => (
            <button data-help="Move the connected displays to this cue in the script."
              key={i}
              onClick={() => command({ type: "seek", wordIndex: m.wordIndex })}
            >
              <Flag size={15} />
              {m.title}
            </button>
          ))}
        </section>
      )}
      <details className="remote-emergency">
        <summary data-help="Open controls for a message that appears over the talent script.">Emergency message</summary>
        <input
          aria-label="Remote emergency message"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <button data-help="Show the entered message over the script on connected displays."
          className="danger-button"
          onClick={() => command({ type: "emergency", text })}
        >
          Show message
        </button>
        <button data-help="Remove the emergency message and reveal the script underneath." onClick={() => command({ type: "emergency", text: "" })}>
          Clear message
        </button>
      </details>
      <p className="remote-footer">
        Keep the operator tab open. This controller can operate the entire room.
      </p>
      <nav className="information-links" aria-label="Service information">
        <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy</a>
        <a href="/legal.html" target="_blank" rel="noopener noreferrer">Legal notice</a>
        <a href="/source.html" target="_blank" rel="noopener noreferrer">Source code</a>
      </nav>
    </main>
  );
}

function WaitingView({ studio, orientation }: { studio: StudioController; orientation?: { flipX: boolean; flipY: boolean } }) {
  return (
    <main className={`waiting-page${orientation ? " talent-page" : ""}`}
      data-flip-x={orientation?.flipX} data-flip-y={orientation?.flipY}
      style={orientation ? { transform: `scale(${orientation.flipX ? -1 : 1},${orientation.flipY ? -1 : 1})` } : undefined}>
      <AudioLines size={38} />
      <h1>Waiting for the operator</h1>
      <p>
        Keep the operator tab open. Your script will appear when this display
        receives the room snapshot.
      </p>
      {studio.status.error && (
        <p role="alert" className="error-text">
          {studio.status.error}
        </p>
      )}
      <span>
        {studio.status.connected
          ? "Connection ready · waiting for script"
          : "Connecting to your room…"}
      </span>
      <button data-help="Reload this window and try to reconnect to the operator room." className="subtle-button" onClick={() => location.reload()}>
        <RefreshCw size={16} />
        Reconnect
      </button>
    </main>
  );
}
